import { WavRecorder } from '@/lib/wavtools';
import api from '@/utils/ky';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  CoachModule,
  CoachSubmodule,
  DEFAULT_COMPANY_NAME,
  ScenarioGenerationMessage,
} from '@/components/coach';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { Chat, Sidebar } from '../../components/chat';
import {
  SALES_FEEDBACK_REQUEST_MESSAGE,
  SALES_MODULES,
} from '../../components/sales';
import { formatFeedbackMessages } from '../../components/ui/feedback';
import { LoadingOverlay } from '../../components/ui/loading';
import { useDeviceId } from '../../hooks/useDeviceId';
import { useCoachStore } from '../../store/coach';

export default function SalesChat() {
  const coachStore = useCoachStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { lang } = useParams();
  const { t } = useTranslation();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const deviceId = useDeviceId();
  const userName = location.state?.userName || 'there';
  const companyName = location.state?.companyName || DEFAULT_COMPANY_NAME;
  const [loadingState, setLoadingState] = useState('initial');
  const [isLoadingDeviceId, setIsLoadingDeviceId] = useState(false);
  const processedMessageIds = useRef<Set<string>>(new Set());
  const [memories, setMemories] = useState<
    Array<{
      memory: string;
      timestamp: Date;
      category?: string;
    }>
  >([]);
  const [currentInstructions, setCurrentInstructions] = useState('');

  // State for conversation management
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [items, setItems] = useState<ItemType[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [selectedModule, setSelectedModule] = useState<CoachModule | null>(
    null,
  );
  const [feedbackMessages, setFeedbackMessages] = useState<any[]>([]);
  const [isFeedbackDiscussion, setIsFeedbackDiscussion] =
    useState<boolean>(false);
  const [isAudioProcessing, setIsAudioProcessing] = useState<boolean>(false);

  // Refs
  const clientRef = useRef<RealtimeClient | null>(null);
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 }),
  );

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const socketRef = useRef(null);

  const startTimeRef = useRef<number>(0);

  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  useEffect(() => {
    initializeConversation();
    posthog.capture('coach_chat_started', {
      code: searchParams.get('code'),
      name: userName,
    });

    return () => {
      const client = clientRef.current;
      setFeedbackMessages([]);
      coachStore.setLastFeedback(null);
      coachStore.setSessionTranscript(null);
      if (client) {
        client.reset();
      }
    };
  }, []);

  const initializeClient = useCallback(() => {
    return new RealtimeClient({
      url: import.meta.env.VITE_SOCKET_URI,
    });
  }, []);

  // Initialize conversation
  const initializeConversation = async () => {
    console.log('Initializing conversation');
    try {
      setLoadingState('connecting');

      const client = await initializeClient();
      clientRef.current = client;

      startTimeRef.current = Date.now();
      setIsConnected(true);

      await client.connect();

      // setSessionPhase(SessionPhase.MAIN_CONVERSATION);
      // Configure session based on intro state
      const config: any = {
        input_audio_transcription: { model: 'whisper-1' },
        turn_detection: null,

        model: 'gpt-4o',
        voice: 'shimmer',
        temperature: 0.8,
      };
      client.updateSession(config);

      client.on('conversation.updated', async ({ item, delta }) => {
        const items = client.conversation.getItems();
        setItems(items);
        // Only process new completed messages
        if (item.status === 'completed') {
          const content =
            item.formatted?.transcript || item.formatted?.text || '';
          if (!content || !item.id) return;

          // Skip if we've already processed this message
          if (processedMessageIds.current.has(item.id)) {
            return;
          }

          // Mark message as processed
          processedMessageIds.current.add(item.id);

          const messages = [
            {
              messageId: item.id,
              role: item.role as 'assistant' | 'user',
              content,
              timestamp: new Date(),
            },
          ];

          // Update conversation with just the new message
          updateConversation({
            messages,
            sessionId: activeSessionId,
            isIC: currentModule?.isIC,
            ...(selectedModule && !activeSessionId
              ? {
                  category: {
                    id: selectedModule.id,
                    title: selectedModule.title,
                    description: selectedModule.description,
                  },
                }
              : {}),
          });
        }
      });

      const currentModule: any =
        selectedModule ??
        SALES_MODULES.find(
          (m) => m.id === (location.state?.category || coachStore.category),
        );
      if (!selectedModule) setSelectedModule(currentModule);

      console.log('currentModule:', currentModule);

      const submodules = currentModule?.submodules?.map(
        (submodule: any, i: number) => {
          const title = submodule.title;
          const description = submodule.description;
          return `${i + 1}. Title: ${title}, description: ${description}.\n`;
        },
      );

      const initialMessage = currentModule?.initialMessage;

      const instructions = currentModule?.instructions
        .replaceAll('{{initialMessage}}', initialMessage)
        .replaceAll('{{userName}}', userName)
        .replaceAll('{{submodules}}', submodules);

      setCurrentInstructions(instructions);

      console.log('Sending instructions:', instructions);

      client.updateSession({ instructions });

      // Send initial message
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: 'Hello',
        },
      ]);

      startTimeRef.current = Date.now();
      setupClientListeners(client);
    } catch (error) {
      console.error('Connection error:', error);
      setError('Failed to connect to AI Coach');
    } finally {
      setLoadingState('ready');
    }
  };

  const { data: memoryData } = useQuery({
    queryKey: ['memories', coachStore.userId],
    queryFn: () =>
      api
        .get('api/sales/memories', {
          searchParams: {
            userId: coachStore.userId,
            limit: 20,
          },
        })
        .json<
          Array<{
            memory: string;
            timestamp: Date;
            category?: string;
          }>
        >(),
    enabled: !!coachStore.userId,
  });

  useEffect(() => {
    if (memoryData) {
      console.log('Memories:', memoryData);
      setMemories(memoryData);

      if (
        currentInstructions?.includes('Previous Context About User') ||
        coachStore.lastFeedback
      ) {
        return;
      } else {
        const client = clientRef.current;
        // Format memories as context
        const memoryContext =
          memoryData.length > 0
            ? `\nPrevious Context About User:\n${memoryData
                .map((m) => m.memory)
                .join('\n\n')}`
            : '';

        const instructions = `${currentInstructions}\n${memoryContext}</instructions>`;
        console.log('Updated instructions:', instructions);
        setCurrentInstructions(instructions);

        client.updateSession({ instructions });
      }
    }
  }, [memoryData]);

  // Mutation for managing conversations
  const { mutate: updateConversation } = useMutation({
    mutationFn: (data: {
      messages: {
        role: 'assistant' | 'user';
        content: string;
        timestamp: Date;
      }[];
      sessionId?: string;
      isIC?: boolean;
      category?: {
        id: string;
        title: string;
        description: string;
      };
    }) => {
      const payload = {
        deviceId,
        messages: data.messages,
        ...(activeSessionId && { sessionId: activeSessionId }),
        ...(data.category && { category: data.category }),
        isIC: data.isIC,
      };

      return api
        .post('api/sales/conversations', {
          json: payload,
          timeout: 30000,
          retry: {
            limit: 2,
            methods: ['post'],
            statusCodes: [500, 502, 503, 504],
          },
        })
        .json<{
          sessionId: string;
          title: string;
        }>();
    },
    onSuccess: (data) => {
      setActiveSessionId(data.sessionId);
      queryClient.invalidateQueries({
        queryKey: ['conversations', deviceId],
      });
    },
    onError: (error) => {
      console.error('Error updating conversation:', error);
      toast.error('Failed to save conversation. Please try again.');
    },
  });

  const { mutate: updateMemory } = useMutation({
    mutationFn: (data: {
      userId: string;
      assistantMessage: string;
      userMessage: string;
      category?: string;
    }) =>
      api
        .post('api/sales/conversations/memory', {
          json: data,
        })
        .json<{ memoryId?: string }>(),
    onError: (error) => {
      console.error('Error updating memory:', error);
      // Non-blocking - don't show error to user
    },
  });

  // When loading an existing conversation
  const handleConversationSelect = async (sessionId: string) => {
    setActiveSessionId(sessionId);

    // Clear processed message IDs when switching conversations
    processedMessageIds.current.clear();

    const conversation: { messages: any[] } = await api
      .get(`api/sales/conversations/${sessionId}`)
      .json();

    // Add existing message IDs to the processed set
    conversation.messages.forEach((msg: any) => {
      if (msg.messageId) {
        processedMessageIds.current.add(msg.messageId);
      }
    });

    setItems(conversation.messages);
  };

  const setupClientListeners = useCallback((client: RealtimeClient) => {
    // Handle conversation updates
    client.on('conversation.updated', async ({ item, delta }) => {
      const items = client.conversation.getItems();
      setItems(items);
    });

    // Handle errors
    client.on('error', (error: any) => {
      console.error('Error Occurred', error);
      setError(error.message || 'An unexpected error occurred');
    });

    setItems(client.conversation.getItems());
  }, []);

  const startRecording = async () => {
    if (searchParams.get('code') === 'test-google-stt') {
      startRecordingStt();
    } else {
      startRecordingWhisper();
    }
  };

  const stopRecording = async () => {
    if (searchParams.get('code') === 'test-google-stt') {
      stopRecordingStt();
    } else {
      stopRecordingWhisper();
    }
  };

  const startRecordingWhisper = async () => {
    if (isRecording) return;
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    // Begin a new audio recording session
    if (wavRecorder.getStatus() === 'ended') {
      // asks for mic permission if not already granted
      await wavRecorder.begin();
    }

    await wavRecorder.record((data) => {
      if (client.isConnected()) {
        client.appendInputAudio(data.mono);
      }
    });
  };

  const stopRecordingWhisper = async () => {
    if (!isRecording) return;
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();

    // End the recording session
    await wavRecorder.end();

    client.createResponse();
  };

  const startRecordingStt = async () => {
    const client = clientRef.current;
    try {
      socketRef.current = new WebSocket(import.meta.env.VITE_STT_WEBSOCKET_URI);

      socketRef.current.onopen = () => {
        console.log('WebSocket connection established.');
      };

      socketRef.current.onmessage = (message) => {
        console.log('Received message:', message.data);
        const data = JSON.parse(message.data);
        if (client) {
          setIsAudioProcessing(false);
          client.sendUserMessageContent([
            {
              type: 'input_text',
              text: data.transcript,
            },
          ]);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Chunk size:', event.data.size); // Log chunk size
          audioChunksRef.current.push(event.data);
        } else {
          console.error('No data captured in this chunk.');
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecordingStt = async () => {
    const mediaRecorder = mediaRecorderRef.current;
    const socket = socketRef.current;

    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsAudioProcessing(true);

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav',
        });
        audioChunksRef.current = [];
        const audioBase64 = await blobToBase64(audioBlob);

        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({
              audio: audioBase64,
              encoding: 'WEBM_OPUS',
              sampleRateHertz: 48000,
              languageCode: 'en-US',
            }),
          );
        }
      };
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      if (!(blob instanceof Blob)) {
        console.error('Invalid input: Not a Blob', blob);
        reject(new Error('Input must be a Blob'));
        return;
      }

      const reader: any = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleTextInputSubmit = () => {
    if (textInput.trim() === '') return;
    sendMessage(textInput);
    setTextInput('');
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTextInput(event.target.value);
  };

  const sendMessage = async (message: string) => {
    const client = clientRef.current;
    if (client) {
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: message,
        },
      ]);
    }

    const getLastAiMessage = (items: any[]): string => {
      const lastAiItem = [...items]
        .reverse()
        .find((item) => item?.role === 'assistant');

      if (lastAiItem?.content?.[0]?.transcript) {
        return lastAiItem.content[0].transcript;
      }

      if (lastAiItem?.content && typeof lastAiItem.content === 'string') {
        return lastAiItem.content;
      }

      return 'AI message'; // Return empty string if no valid message found
    };

    if (activeSessionId && items.length >= 2) {
      console.log('items:', items);
      const lastAiMessage: string = getLastAiMessage(items);

      updateMemory({
        userId: coachStore.userId,
        assistantMessage: lastAiMessage,
        userMessage: message,
        category: selectedModule?.id,
      });
    }
  };

  // Create user mutation
  const { mutate: createUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
      code: string;
      companyName: string;
    }) =>
      api
        .post('api/sales/users', {
          json: data,
        })
        .json<{
          userId: string;
          deviceId: string;
          companyName: string;
        }>(),
    onSuccess: (data, variables) => {
      console.log('User created ::: data:', data, ', variables:', variables);
      coachStore.setUser({
        userId: data.userId,
        deviceId: data.deviceId,
        name: variables.name,
        function: variables.function,
        category: location.state?.category,
        companyName: data.companyName,
      });

      posthog.capture('coach_chat_user_created', {
        code: searchParams.get('code'),
        name: userName,
        userId: data.userId,
        deviceId: data.deviceId,
        function: variables.function,
        category: location.state?.category,
        companyName: variables.companyName,
      });
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
      toast.error('Failed to start session. Please try again.');
    },
  });

  // Generate scenario mutation (updated with userId)
  const { mutate: generateScenario, isPending: isGeneratingScenario } =
    useMutation({
      mutationFn: (data: {
        category: any;
        subCategory: any;
        conversation: string;
        sessionId: string;
        language: string;
      }) =>
        api
          .post('api/sales/scenarios/generate', {
            json: data,
            timeout: 9999999,
            retry: {
              limit: 2,
              methods: ['post'],
            },
          })
          .json<{ sessionId: string }>(),
      onSuccess: async (data) => {
        console.log('Generated scenario:', data);
        posthog.capture('coach_practice_scenario_generated', {
          code: searchParams.get('code'),
          userId: coachStore.userId,
          sessionId: activeSessionId,
        });

        const pathToRoleplay =
          '/sales/roleplay' +
          `?sessionId=${activeSessionId}${code ? `&code=${code}` : ''}`;
        navigate(pathToRoleplay);
      },
    });

  const handleSubmoduleSelect = async (submodule: CoachSubmodule) => {
    // Get conversation transcript
    const conversationText = items
      .map(
        (item) =>
          `${item.role}: ${
            item.formatted?.text || item.formatted?.transcript || ''
          }`,
      )
      .join('\n');

    // Get current category from selected module
    const currentCategory = selectedModule;

    setItems((prev: any) => [
      ...prev,
      {
        role: 'assistant',
        content: '',
        id: Date.now().toString(),
        isGeneratingScenario: true, // Add custom flag
      },
    ]);

    posthog.capture('coach_submodule_practice_selected', {
      code: searchParams.get('code'),
      userId: coachStore.userId,
      sessionId: coachStore.browserSessionId,
      categoryId: currentCategory.id,
      categoryTitle: currentCategory.title,
      subModuleId: submodule.id,
      subModuleTitle: submodule.title,
      subModuleType: submodule.type,
    });

    // Generate scenario with context
    generateScenario({
      category: {
        id: currentCategory.id,
        title: currentCategory.title,
        description: currentCategory.description,
      },
      subCategory: {
        id: submodule.id,
        title: submodule.title,
        type: submodule.type,
      },
      conversation: conversationText,
      sessionId: activeSessionId,
      language: lang,
    });
  };

  const handleDiscussFeedback = () => {
    const client = clientRef.current;
    setIsFeedbackDiscussion(true);
    if (client) {
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: SALES_FEEDBACK_REQUEST_MESSAGE.replace(
            '{{feedbackMessage}}',
            feedbackMessages.map((item) => item.content).join('\n'),
          ).replace('{{transcript}}', coachStore.sessionTranscript),
        },
      ]);
    }
    setFeedbackMessages([]);
    coachStore.setLastFeedback(null);
  };

  useEffect(() => {
    async function initializeUser() {
      if (location.state?.userName && !coachStore.userId) {
        setIsLoadingDeviceId(true);
        try {
          // Initialize FingerprintJS
          const fp = await FingerprintJS.load();
          // Get visitor identifier
          const result = await fp.get();
          const deviceId = result.visitorId;

          const userParams = {
            name: location.state.userName,
            deviceId: deviceId,
            function: 'other', // Default function
            code: searchParams.get('code'),
            companyName: location.state.companyName || DEFAULT_COMPANY_NAME,
          };
          createUser(userParams);
        } catch (error) {
          console.error('Error generating device ID:', error);
          // Fallback to random UUID if fingerprinting fails
          const userParams = {
            name: location.state.userName,
            deviceId: uuidv4(),
            function: 'other',
            code: searchParams.get('code'),
            companyName: location.state.companyName || DEFAULT_COMPANY_NAME,
          };
          createUser(userParams);
        } finally {
          setIsLoadingDeviceId(false);
        }
      }
    }

    initializeUser();
  }, [location.state?.userName]);

  useEffect(() => {
    const currentModule: any = SALES_MODULES.find(
      (m) => m.id === (location.state?.category || coachStore.category),
    );
    setSelectedModule(currentModule);
  }, [location.state?.category]);

  useEffect(() => {
    const displayFeedback = async (feedback: any) => {
      const sections1 = formatFeedbackMessages(feedback, t);
      const sections = sections1.reduce((acc, curr) => {
        const existing = acc.find((s) => s.id === curr.id);
        if (existing) {
          return acc.map((s) => (s.id === curr.id ? { ...s, ...curr } : s));
        }
        return [...acc, curr];
      }, [] as typeof sections1);

      // Display sections sequentially with animation
      for (const section of sections) {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Delay between sections

        setFeedbackMessages((prev: any) => [
          ...prev,
          {
            id: section.id,
            role: 'assistant',
            content:
              section.type === 'header'
                ? `${section.emoji} <b>${section.content}</b>`
                : `• ${section.content}`,
            type: 'text',
          },
        ]);
      }

      // Add final options after all sections
      setFeedbackMessages((prev: any) => [
        ...prev,
        {
          id: 'feedback-options',
          role: 'assistant',
          type: 'options',
          content: '',
          options: [
            {
              value: 'practice_again',
              label: t('coach.chat.feedbackOptions.practice_again.label'),
              description: t(
                'coach.chat.feedbackOptions.practice_again.description',
              ),
            },
            {
              value: 'discuss_feedback',
              label: t('coach.chat.feedbackOptions.discuss_feedback.label'),
              description: t(
                'coach.chat.feedbackOptions.discuss_feedback.description',
              ),
            },
          ],
        },
      ]);
    };

    if (location.state?.fromRolePlay && coachStore.lastFeedback) {
      displayFeedback(coachStore.lastFeedback);
      // Clear feedback after starting display
      // coachStore.clearSessionData();
    }
  }, [location.state?.fromRolePlay, coachStore.lastFeedback]);

  if (isGeneratingScenario) {
    return <ScenarioGenerationMessage />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Single Left Sidebar combining navigation and conversations */}
      <Sidebar
        userName={userName}
        companyName={companyName}
        activeSessionId={activeSessionId}
        onConversationSelect={handleConversationSelect}
        useCase="sales"
      />

      <div className="flex-1 flex flex-col bg-gray-50">
        <Chat
          items={items}
          textInput={textInput}
          handleTextInputChange={handleTextInputChange}
          handleTextInputSubmit={handleTextInputSubmit}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isRecording={isRecording}
          isConnected={isConnected}
          selectedModule={selectedModule}
          handleSubmoduleSelect={handleSubmoduleSelect}
          feedbackMessages={feedbackMessages}
          handleDiscussFeedback={handleDiscussFeedback}
          isFeedbackDiscussion={isFeedbackDiscussion}
          isAudioProcessing={isAudioProcessing}
          isLoadingDeviceId={isLoadingDeviceId}
          useCase="sales"
        />
      </div>
      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && <LoadingOverlay state={loadingState} />}
      </AnimatePresence>
    </div>
  );
}
